import type {
  IconSpec,
  LaunchItem,
  ResolvedCommand,
  ResolvedSearchFilter,
  ResolvedSearchSection,
  SearchHitAnchor,
  WhenContext,
} from '@hibro/shared';
import {
  cn,
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInputControl,
  ComboboxItem,
  ComboboxList,
  CommandShortcut,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  Spinner,
} from '@hibro/ui-client';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { TitleResolver } from '../../commands/command-query';
import { useTranslator } from '../../i18n/i18n';
import { resolveLaunchItemLabel } from '../../launcher/launch-activation';
import { filtersForTab } from '../../search/filters';
import { highlightSegments } from '../../search/highlight';
import { commandSuggestionNames } from '../../search/providers/command-provider';
import { openCommandSuggestionNames } from '../../search/providers/launch-providers';
import {
  appTabModuleIds,
  resolveSectionTitle,
  sectionOrderForTab,
} from '../../search/providers/module-provider';
import { RECENT_DISPLAY_LIMIT } from '../../search/providers/recent-provider';
import type { SearchSectionResults } from '../../search/run-search';
import {
  focusDidNotLeave,
  holdsFaceOpen,
  isInPlaceResult,
  isSearchFaceResult,
  pickKeepsFaceOpen,
  type SearchResult,
  type SearchSeed,
  type SearchTab,
  searchTabFromSeed,
  SECTION_COMMAND,
  SECTION_RECENT,
  shouldCloseForSurfaceFrameFocus,
} from '../../search/search-contract';
import { SearchPersonAffix } from '../../search/search-person-affix';
import { suggestCommands } from '../../search/suggest';
import { useFilterOptions } from '../../search/use-filter-options';
import { useSearch, type UseSearchOptions } from '../../search/use-search';
import { ACTION_ROW_CLASS, ActionRowContent } from './action-row';
import { SearchFilterBar } from './search-filters';
import { cycleSearchTab } from './tab-cycle';

/** Rows a section shows before the user asks for more (需求 FR-14 §4.2.1). */
const DEFAULT_ROW_CAP = 8;
const SECTION_ROW_CAP: Readonly<Record<string, number>> = {
  [SECTION_RECENT]: RECENT_DISPLAY_LIMIT,
  [SECTION_COMMAND]: DEFAULT_ROW_CAP,
};

/**
 * Rows fetched per provider, well above the cap so a section knows it has more
 * to give and MORE can expand in place with no second round trip.
 *
 * Deliberately not `cap + 1`: with only a probe row, expanding would have to
 * re-query, and the short-query case this exists for (typing `1`, whose hit sits
 * far down the list) would still be one keystroke away from a refetch. The cost
 * is a wider payload per keystroke, already debounced — worth it against a
 * result the user provably could not reach. Beyond this lives the "view all"
 * panel, which asks with no limit at all.
 */
const FETCH_LIMIT = 50;

/** How long the input border stays warning-coloured (需求 §4.2.3). */
const WARNING_FLASH_MS = 1500;

/**
 * Module-scoped so its identity is stable: `useSearch` keys its parsed query —
 * and therefore the effect that runs the providers — off this value, and an
 * inline object would re-run the search on every render.
 */
const ALL_TAB: SearchTab = { kind: 'all' };

/** What the unknown-command state offers as a near miss. */
export type SearchSuggestion = ResolvedCommand | LaunchItem;

export interface HeaderSearchProps extends Pick<
  UseSearchOptions,
  'providers' | 'onRecord' | 'onRun'
> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Text to put in the field, applied once per {@link SearchSeed.token}.
   *
   * How the two search commands differ (见 `workbench.tsx`): one seeds `/` and
   * lands in command mode, the other seeds nothing. Keyed by a token rather than
   * compared by value so pressing the palette chord over an already-open box
   * still switches it into command mode.
   */
  seed?: SearchSeed;
  /** Toggles the AI auxiliary area; omitted hides the trailing button. */
  onToggleAi?: () => void;
  /**
   * Module-declared search sections (需求 §5.3.2). Drives the app tab strip and
   * the section headings inside an app tab; empty means no strip at all.
   */
  searchSections: readonly ResolvedSearchSection[];
  /**
   * Module-declared filters (需求 §5.3.2 ③). Only the active app tab's own are
   * drawn; the All tab has none, since a filter is one module's vocabulary.
   */
  searchFilters: readonly ResolvedSearchFilter[];
  /** Runs a `picker` filter's `optionsCommand`; the module dispatcher, so it auto-activates. */
  executeCommand: (id: string, ...args: unknown[]) => Promise<unknown>;
  /**
   * Whether a platform overlay is currently presented. Drives the hold that
   * keeps the dropdown alive under an in-place hit's card (见 `holdsFaceOpen`);
   * the workbench owns the OverlaySurface stack, so it is the only place that
   * knows.
   */
  platformOverlayOpen?: boolean;
  /** Launchables, for an app tab's label and glyph. */
  launchItems: readonly LaunchItem[];
  /** Candidate pool for the unknown-command suggestions (需求 §4.2.3). */
  suggestionSources: {
    commands: readonly ResolvedCommand[];
    context: WhenContext;
    resolveTitle: TitleResolver;
    launchItems: readonly LaunchItem[];
  };
  onRunSuggestion: (suggestion: SearchSuggestion) => void;
}

/**
 * Global search box in the workbench header (需求 FR-14 §4 / 见 03 header 槽位).
 *
 * Presentation only: the field, the dropdown, per-section row caps, keyword
 * highlighting and the three input states. What matches and in what order lives
 * in `useSearch` and the providers behind it, which is what lets a future "view
 * all commands" panel reuse the behaviour with different markup.
 *
 * Built on `Combobox` rather than the `Command` palette primitive because the
 * dropdown has to escape the header's clipping (a 36px dockview edge panel), so
 * the list is portalled — and cmdk resolves its items by querying the DOM under
 * its own root, leaving a portalled list with nothing for keyboard navigation to
 * walk. The combobox pattern is built for this topology: the input in one place,
 * the listbox in another, `aria-activedescendant` wiring between them (见
 * `ui-component-sourcing`: pick the APG pattern first, then the library).
 */
export function HeaderSearch({
  providers,
  onRecord,
  onRun,
  open,
  onOpenChange,
  seed,
  onToggleAi,
  searchSections,
  searchFilters,
  executeCommand,
  platformOverlayOpen = false,
  launchItems,
  suggestionSources,
  onRunSuggestion,
}: HeaderSearchProps): ReactNode {
  const { t, title } = useTranslator();
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * The popup's anchor. Base UI would otherwise anchor to the inset text
   * control, which makes the popup's centre move relative to the whole field.
   * The shell stays the positional anchor while the popup keeps its own fixed
   * width, even when the responsive title-bar slot narrows the field.
   */
  const fieldRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<SearchTab>(ALL_TAB);
  /** Sections the user asked to see in full; capped again on close / tab switch. */
  const [expanded, setExpanded] = useState<readonly string[]>([]);
  /** Filter whose popover is open, or null. */
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  /** The open picker's candidate query — its own field's, not the omnibar's. */
  const [filterQuery, setFilterQuery] = useState('');
  /**
   * Row elements by result key, so an `inPlace` pick can say where it was.
   *
   * A registry rather than reading the press event: Enter picks the highlighted
   * row and has no coordinates, and both paths must anchor identically or the
   * card would jump depending on how it was chosen.
   */
  const rowElements = useRef(new Map<string, HTMLElement>());
  /**
   * The row Base UI currently highlights, read **inside event handlers**.
   *
   * A ref rather than state for the reason the profile card's guard records
   * (contacts `overlay-guard.ts`): the close that has to be judged arrives in
   * the same batch as the state update that would describe it, so a handler
   * would read the previous render's value.
   */
  const highlightedRow = useRef<SearchResult | undefined>(undefined);
  /**
   * An in-place pick is outstanding: from the click until the layer it opened is
   * gone. While it is set, the ambient closes are held (见 `holdsFaceOpen`).
   *
   * Armed here rather than off `platformOverlayOpen` alone because the overlay
   * takes focus in the same commit that reports itself open, so a prop read in
   * the handler can still be `false`. Arming early is safe: the click landed
   * *inside* the popup, so nothing tries to close the dropdown during the
   * round trip to the extension host.
   */
  const inPlacePending = useRef(false);

  // Disarm once the layer has actually come and gone. Keyed on the transition,
  // not on `false` alone, so the initial render does not clear a fresh arm.
  const sawOverlay = useRef(false);
  useEffect(() => {
    if (platformOverlayOpen) {
      sawOverlay.current = true;
      return;
    }
    if (sawOverlay.current) {
      sawOverlay.current = false;
      inPlacePending.current = false;
    }
  }, [platformOverlayOpen]);
  const measureRow = (key: string): SearchHitAnchor | undefined => {
    const rect = rowElements.current.get(key)?.getBoundingClientRect();
    return rect
      ? { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
      : undefined;
  };
  const sectionOrder = useMemo(
    () => sectionOrderForTab(searchSections, activeTab),
    [searchSections, activeTab],
  );
  const search = useSearch({
    providers,
    tab: activeTab,
    sectionOrder,
    limit: FETCH_LIMIT,
    active: open,
    // An open panel is an edit in progress: the values it writes only reach
    // the providers once it closes (见 `UseSearchOptions.editingFilters`).
    editingFilters: openFilterId !== null,
    ...(onRecord ? { onRecord } : {}),
    onRun,
  });

  // A shortcut or the built-in command opened the box. The popup is controlled,
  // so nothing else would move the caret into the field.
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  // Apply whatever the invoking command wanted the field to start with. Keyed on
  // the token alone: the text repeats (`/` every time the palette chord is hit)
  // and re-running on the object's identity would fight the user's typing.
  const seedToken = seed?.token;
  useEffect(() => {
    if (seedToken === undefined) {
      return;
    }
    search.setQuery(seed?.text ?? '');
    const tab = searchTabFromSeed(seed);
    if (tab) {
      setActiveTab(tab);
      setExpanded([]);
      // Same drop as a click on the strip: a filter set on the previous tab
      // must not keep narrowing after the seed moved us.
      setOpenFilterId(null);
      setFilterQuery('');
      search.clearFilters();
    }
    // The caret belongs after the seeded prefix, which is where focus lands
    // anyway — but the field may already hold a longer query being replaced.
    inputRef.current?.setSelectionRange(-1, -1);
    // `search` is rebuilt every render; keying on the token is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedToken]);

  const appTabs = useMemo(
    () =>
      appTabModuleIds(searchSections).map((moduleId) => {
        // The app's name has one source of truth, its launchable (见 21) — no
        // second label field on the search declaration. A module with no
        // activity-bar entry falls back to its id.
        const item = launchItems.find(
          (candidate) => candidate.moduleId === moduleId,
        );
        return {
          moduleId,
          label: item ? resolveLaunchItemLabel(item, { t }) : moduleId,
          icon: item?.icon,
        };
      }),
    [searchSections, launchItems, t],
  );

  /**
   * The strip in keyboard order, All first. Memoised because `useSearch` keys its
   * parsed query off the active tab's identity — rebuilding these objects every
   * render would re-run every provider on every keystroke.
   */
  const tabOrder = useMemo<readonly SearchTab[]>(
    () => [
      ALL_TAB,
      ...appTabs.map(
        (tab): SearchTab => ({ kind: 'app', moduleId: tab.moduleId }),
      ),
    ],
    [appTabs],
  );

  /** This tab's filters, in declared order. Empty on the All tab. */
  const tabFilters = useMemo(
    () => filtersForTab(searchFilters, activeTab),
    [searchFilters, activeTab],
  );
  const openFilter =
    tabFilters.find((filter) => filter.id === openFilterId) ?? null;
  const filterOptions = useFilterOptions(
    openFilter,
    filterQuery,
    executeCommand,
  );

  // Section headings for the active app tab, keyed by the section id the
  // providers report (`${moduleId}:${id}`).
  const sectionTitles = useMemo(
    () =>
      new Map(
        searchSections.map((section) => [
          section.key,
          resolveSectionTitle(section, title),
        ]),
      ),
    [searchSections, title],
  );

  /**
   * 每段真正要画出来的那几行，**按渲染顺序**，行数上限在这里算一次。
   *
   * 既喂给下面的分段渲染，也整成一条扁平表交给 Combobox 的 `items`。两者必须是同一份：`items`
   * 是键盘导航的索引依据，和 DOM 顺序错一位，↓ 就会跳到别的行上。
   */
  const visibleSections = useMemo(
    () =>
      search.sections.map((section) => {
        const cap = SECTION_ROW_CAP[section.section] ?? DEFAULT_ROW_CAP;
        return {
          section,
          shown: expanded.includes(section.section)
            ? section.results
            : section.results.slice(0, cap),
        };
      }),
    [search.sections, expanded],
  );

  /**
   * 权威的候选表。**不传它，`autoHighlight` 会时灵时不灵**：Combobox 只能靠每个 item 自注册来
   * 攒这张表，而结果列表一变（分段异步到达、行数增减）就有一个窗口期表比 `activeIndex` 短，
   * 于是高亮被抹成「没有选中」。用户看到的还是上一帧高亮的首行，按 ↓ 却只是把高亮补回第一行
   * ——就是「第一次要按两下」。传了 `items` 之后这张表由它直接采纳，上游那条「避免要按两次
   * ArrowDown」的护栏（挂在 `hasItems` 上）也才会生效。
   *
   * `filter={null}` 保证它不会再过滤一遍（源码里就是 `() => true`），顺序原样。
   */
  const items = useMemo(
    () => visibleSections.flatMap((entry) => entry.shown),
    [visibleSections],
  );
  const hasErrorRows = search.sections.some(
    (section) => section.errors.length > 0,
  );

  // 光一个 `/` 不算「未知命令」——用户还没敲出命令名，那是「列出全部命令」的意思。少了这道
  // 判据，命令段一旦为空就会显示「未找到命令「/」」，紧跟着又劝你「输入 / 查看可用命令」。
  const unknownCommand =
    search.commandMode && search.empty && search.typedCommand.trim().length > 0;
  const flashing = useWarningFlash(unknownCommand ? search.typedCommand : null);

  const suggestions = useMemo(() => {
    if (!unknownCommand) {
      return [];
    }
    return suggestCommands<SearchSuggestion>(search.typedCommand, [
      ...commandSuggestionNames(suggestionSources),
      ...openCommandSuggestionNames(suggestionSources.launchItems),
    ]);
  }, [unknownCommand, search.typedCommand, suggestionSources]);

  const closeFilterPanel = (): void => {
    setOpenFilterId(null);
    setFilterQuery('');
  };

  /**
   * Open / close one filter's popover, putting the caret back in the omnibar on
   * the way out.
   *
   * The refocus is load-bearing: Base UI returns focus to the trigger, which is
   * a chip. Focus would then sit on a `tabIndex={-1}` button inside the popup —
   * the dropdown survives (focus never left it) but typing goes nowhere, which
   * is the lost-caret symptom the inline panel had for a different reason.
   */
  const openFilterPanel = (id: string | null): void => {
    setFilterQuery('');
    setOpenFilterId(id);
    if (id === null) {
      inputRef.current?.focus();
    }
  };

  const close = (): void => {
    onOpenChange(false);
    setActiveTab(ALL_TAB);
    setExpanded([]);
    closeFilterPanel();
    search.reset();
  };

  /**
   * Iframe clicks never become Base UI `outside-press`, and Chromium does not
   * fire parent `blur` / `focusin` when focus moves into a frame — it only
   * updates `document.activeElement`. Watch that while open. Do not close
   * until the omnibar has actually held focus once: opening over an already-
   * focused frame would otherwise shut the dropdown on the first frame.
   *
   * Refs rather than listing `close` in the effect: `useSearch` rebuilds
   * every render, and restarting the loop on every keystroke is how this
   * would miss the leave that arrived between cancel and start.
   */
  const closeRef = useRef(close);
  closeRef.current = close;
  const childLayerOpenRef = useRef(false);
  childLayerOpenRef.current = openFilterId !== null || inPlacePending.current;
  useEffect(() => {
    if (!open) {
      return;
    }
    let sawField = false;
    let raf = 0;
    const tick = (): void => {
      const active = document.activeElement;
      if (
        active === inputRef.current ||
        (active !== null && fieldRef.current?.contains(active) === true)
      ) {
        sawField = true;
      }
      if (
        sawField &&
        shouldCloseForSurfaceFrameFocus(active, childLayerOpenRef.current)
      ) {
        closeRef.current();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const selectTab = (tab: SearchTab): void => {
    setActiveTab(tab);
    setExpanded([]);
    // Filters belong to the tab that declared them, so leaving it drops both
    // the open editor and the values — a "from" set on the chat tab must not
    // quietly still be narrowing anything after a switch.
    closeFilterPanel();
    search.clearFilters();
  };

  /**
   * `Tab` walks the app strip, `Shift+Tab` walks it backwards (需求 §5.2).
   *
   * Tab is what is left: ←→ move the caret inside the field and ↑↓ drive the
   * result list. Taking it means focus no longer leaves the box on Tab, so this
   * only binds while the popup is open **and** the strip has a second tab to go
   * to — otherwise Tab keeps its normal meaning. `Esc` closes the popup, which
   * is what restores the usual focus order.
   */
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Tab' || !open || tabOrder.length < 2) {
      return;
    }
    event.preventDefault();
    selectTab(cycleSearchTab(tabOrder, activeTab, event.shiftKey ? -1 : 1));
  };

  const toggleExpanded = (section: string): void => {
    setExpanded((current) =>
      current.includes(section)
        ? current.filter((entry) => entry !== section)
        : [...current, section],
    );
  };

  return (
    <Combobox<SearchResult>
      value={null}
      // The kernel already filtered and ranked; Base UI must not re-filter.
      filter={null}
      items={items}
      // `'always'`, not the boolean. `true` is `'input-change'`: highlight on
      // the keystroke, then stop. Commands land in the same turn so that works;
      // a module section crosses a process boundary and arrives after the list
      // was empty, at which point the empty-list guard has already cleared the
      // index. `'always'` puts the highlight back on the first row whenever the
      // list is open and nothing is highlighted — the same "Enter runs the best
      // match" the palette had, for rows that were not there yet when the user
      // typed.
      autoHighlight="always"
      // Rows are objects, and picking one fills the field with its string form
      // before we clear it. Without this the field flashes `[object Object]`.
      itemToStringLabel={(result) => result.title}
      inputValue={search.query}
      onInputValueChange={(next, details) => {
        // Picking a row makes Base UI write the row's label into the field
        // (that is what `itemToStringLabel` is for). Harmless while every pick
        // closed the box and reset the query — but an `inPlace` pick keeps it
        // open, and the user would watch their query be replaced by the title
        // of the thing they just glanced at.
        if (details.reason === 'item-press') {
          return;
        }
        search.setQuery(next);
      }}
      open={open}
      // Which row is about to be picked. Base UI highlights on hover as well as
      // on arrow keys, so this covers both ways of choosing one — and unlike
      // `onValueChange` it has certainly run by the time the close arrives.
      onItemHighlighted={(value) => {
        highlightedRow.current = value ?? undefined;
      }}
      onOpenChange={(next, details) => {
        // Every close request is judged by `holdsFaceOpen` — see there for why
        // `item-press` is in scope at all (Base UI closes itself on a pick, and
        // `open` is controlled, so not calling `close()` achieves nothing).
        const childLayerOpen = openFilterId !== null || inPlacePending.current;
        const pickedInPlace =
          highlightedRow.current !== undefined &&
          isInPlaceResult(highlightedRow.current);
        const pickedKeepsOpen =
          highlightedRow.current !== undefined &&
          isSearchFaceResult(highlightedRow.current);
        // The field is enough to answer this: the reason that matters lands
        // with focus back on the omnibar input, which lives inside it. Focus
        // inside the popup never produces a `focus-out` — Base UI counts the
        // popup as part of the combobox and reports `item-press` instead.
        const stayed = focusDidNotLeave(fieldRef.current);
        const held =
          !next &&
          holdsFaceOpen(details.reason, {
            childLayerOpen,
            pickedInPlace,
            pickedKeepsOpen,
            focusDidNotLeave: stayed,
          });
        if (held) {
          return;
        }
        onOpenChange(next);
        if (!next) {
          // Same reset as `close()`. Outside-press lands here instead of
          // there; leaving the tab would make the next open skip All, which
          // is where recent searches and apps live.
          setActiveTab(ALL_TAB);
          setExpanded([]);
          closeFilterPanel();
          search.reset();
        }
      }}
      onValueChange={(result) => {
        if (!result) {
          return;
        }
        // An `inPlace` action shows something *beside* the row (a profile
        // card), so tearing the dropdown down would throw away the query the
        // user is still reading results from. The two search-face commands
        // reseed this same box (command mode vs empty state) and must stay
        // too — closing after `openSearch` is what made those rows look dead.
        // Only `inPlace` arms the child-layer hold; a reseed never opens one.
        inPlacePending.current = isInPlaceResult(result);
        search.select(result, measureRow(result.key));
        if (!pickKeepsFaceOpen(result)) {
          close();
        }
      }}
      inputRef={inputRef}
    >
      <InputGroup
        ref={fieldRef}
        style={{ WebkitAppRegion: 'no-drag' }}
        className={cn(
          'h-6.5 max-w-105 px-2 py-1 rounded-lg bg-(--app-color-bg-surface) text-(--app-color-text-primary)',
          flashing
            ? 'border-(--app-color-warning)'
            : 'border-(--app-color-border)',
        )}
      >
        <InputGroupAddon className="p-0 text-(--app-color-text-muted)">
          <Icon className="size-4" spec={{ type: 'builtin', id: 'search' }} />
        </InputGroupAddon>
        <ComboboxInputControl
          className="h-4 pl-1 py-0 text-xs placeholder:text-(--app-color-text-muted)"
          placeholder={t('workbench.search.placeholder')}
          onKeyDown={onInputKeyDown}
        />
        {onToggleAi ? (
          <InputGroupAddon align="inline-end" className="p-0">
            <InputGroupButton
              className="cursor-pointer p-0"
              onClick={onToggleAi}
            >
              <Icon className="size-4" spec={{ type: 'builtin', id: 'ai' }} />
            </InputGroupButton>
          </InputGroupAddon>
        ) : null}
      </InputGroup>
      {/* The field may shrink between crowded title-bar sides, but the result
          surface deliberately stays at the design width (需求 §4.3). */}
      <ComboboxContent anchor={fieldRef} align="center" className="w-105 p-0">
        {/* 需求 §4.3: at most 60% of the workbench tall, scrolling inside. */}
        <div className="flex max-h-[60vh] flex-col">
          {/* No app has registered a section -> no strip at all, the same
              occupancy rule the auxiliaryBar follows (见 03). */}
          {appTabs.length > 0 ? (
            <AppTabStrip
              tabs={appTabs}
              active={activeTab}
              onSelect={selectTab}
            />
          ) : null}
          <SearchFilterBar
            filters={tabFilters}
            values={search.filters}
            openFilterId={openFilterId}
            onOpenFilter={openFilterPanel}
            onChange={(id, value) => search.setFilter(id, value)}
            options={filterOptions}
            query={filterQuery}
            onQueryChange={setFilterQuery}
            resolveTitle={title}
          />
          {/* No padding: rows carry their own, so the highlight band and the
              rules between rows run edge to edge (对齐设计稿). The list stays
              put while a filter popover is up — the popover floats over it, so
              the user can still see what they are narrowing. */}
          <ComboboxList className="max-h-none flex-auto overflow-y-auto p-0">
            {visibleSections.map(({ section, shown }) => (
              <SearchSection
                key={section.section}
                section={section}
                shown={shown}
                terms={search.terms}
                moduleTitle={sectionTitles.get(section.section)}
                expanded={expanded.includes(section.section)}
                onToggleExpanded={() => toggleExpanded(section.section)}
                rowElements={rowElements.current}
              />
            ))}
          </ComboboxList>
          {/* Searching covers the run while the list is still empty — local
              providers often return [] first, modules later. Empty waits until
              every provider has settled or exceeded PROVIDER_WAIT_MS. */}
          {!openFilter && search.searching && items.length === 0 ? (
            <SearchingState />
          ) : !openFilter &&
            search.empty &&
            search.query.trim().length > 0 &&
            !hasErrorRows ? (
            <EmptyState
              query={search.query}
              commandMode={search.commandMode}
              typedCommand={search.typedCommand}
              suggestions={suggestions}
              onRunSuggestion={(suggestion) => {
                onRunSuggestion(suggestion);
                close();
              }}
            />
          ) : null}
          <FooterBar />
        </div>
      </ComboboxContent>
    </Combobox>
  );
}

/**
 * Tabs across the top of the dropdown: All plus one per app that registered a
 * section (需求 §5.2). The axis is per-application, not per content type — an
 * app's own sections live inside its tab.
 */
function AppTabStrip({
  tabs,
  active,
  onSelect,
}: {
  tabs: readonly { moduleId: string; label: string; icon?: IconSpec }[];
  active: SearchTab;
  onSelect: (tab: SearchTab) => void;
}): ReactNode {
  const { t } = useTranslator();
  /**
   * Underline rather than a filled pill (对齐设计稿). The rule lives on `after`
   * so it hugs the label's own box: inset by the same amount as the horizontal
   * padding, it spans the text and glyph but not the gap to the next tab.
   */
  const tabClass = (selected: boolean): string =>
    cn(
      'relative flex h-8 shrink-0 cursor-pointer items-center gap-1 px-2.5 text-[13px] leading-5',
      selected
        ? 'font-medium text-(--app-color-accent) after:absolute after:inset-x-2.5 after:bottom-0 after:h-[1.6px] after:rounded-t after:bg-(--app-color-accent)'
        : 'text-(--app-color-text-faint)',
    );
  /**
   * The strip lives inside the combobox popup, so a tab must never take focus:
   * the combobox closes on `focusOut`, and a plain button stealing focus from
   * the input dismissed the whole dropdown on the click that was supposed to
   * switch tabs. `preventDefault` on mousedown keeps the caret in the input
   * (the click still fires), and `tabIndex={-1}` keeps the strip out of the tab
   * order — per APG, a combobox keeps focus on its input.
   */
  const keepFocus = (event: { preventDefault: () => void }): void => {
    event.preventDefault();
  };
  return (
    <div className="flex shrink-0 items-center overflow-x-auto border-b border-(--app-color-border) px-3">
      <button
        type="button"
        tabIndex={-1}
        aria-pressed={active.kind === 'all'}
        className={tabClass(active.kind === 'all')}
        onMouseDown={keepFocus}
        onClick={() => onSelect({ kind: 'all' })}
      >
        {t('workbench.search.tab.all')}
      </button>
      {tabs.map((tab) => {
        const selected =
          active.kind === 'app' && active.moduleId === tab.moduleId;
        return (
          <button
            key={tab.moduleId}
            type="button"
            tabIndex={-1}
            aria-pressed={selected}
            className={tabClass(selected)}
            onMouseDown={keepFocus}
            onClick={() => onSelect({ kind: 'app', moduleId: tab.moduleId })}
          >
            {tab.icon ? <Icon size={16} spec={tab.icon} /> : null}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function SearchSection({
  section,
  shown,
  terms,
  moduleTitle,
  expanded,
  onToggleExpanded,
  rowElements,
}: {
  section: SearchSectionResults;
  /** 本段实际要画的行，行数上限已由父层算过（同一份也喂给 Combobox 的 `items`）。 */
  shown: readonly SearchResult[];
  terms: readonly string[];
  /** Resolved heading for a module-declared section; absent for platform ones. */
  moduleTitle?: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Where each drawn row registers itself, for anchoring an in-place action. */
  rowElements: Map<string, HTMLElement>;
}): ReactNode {
  const { t } = useTranslator();
  const hidden = section.results.length - shown.length;
  if (shown.length === 0 && section.errors.length === 0) {
    return null;
  }
  return (
    <ComboboxGroup className="p-0">
      <ComboboxGroupLabel className="sticky top-0 z-10 flex items-center justify-between bg-popover px-3 py-1.5 font-normal text-(--app-color-text-muted)">
        <span>{moduleTitle ?? t(sectionLabelKey(section.section))}</span>
        {hidden > 0 || expanded ? (
          <button
            type="button"
            // Same focus discipline as the tab strip: the combobox closes on
            // `focusOut`, so a control inside the popup must leave the caret
            // in the input.
            tabIndex={-1}
            aria-expanded={expanded}
            className="cursor-pointer text-[11px] text-(--app-color-text-faint) hover:text-(--app-color-text-primary)"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onToggleExpanded}
          >
            {expanded
              ? t('workbench.search.less')
              : t('workbench.search.more', { count: hidden })}
          </button>
        ) : null}
      </ComboboxGroupLabel>
      {/* A failed provider is shown rather than folded into "no hits": a broken
          source has to look broken (见 `no-implicit-fallbacks`). */}
      {section.errors.map((failure) => (
        <div
          key={failure.providerId}
          className="px-3 py-1.5 text-[11px] text-(--app-color-danger)"
        >
          {t('workbench.search.providerFailed', {
            provider: failure.providerId,
          })}
        </div>
      ))}
      {shown.map((result) => (
        <ResultRow
          key={result.key}
          result={result}
          terms={terms}
          rowElements={rowElements}
        />
      ))}
    </ComboboxGroup>
  );
}

function sectionLabelKey(section: string): string {
  switch (section) {
    case SECTION_RECENT:
      return 'workbench.search.section.recent';
    case SECTION_COMMAND:
      return 'workbench.search.section.command';
    default:
      // A module-declared section supplies its own resolved heading (see
      // `moduleTitle`); this is only reached if that lookup came up empty.
      return section;
  }
}

function highlightQuery(text: string, terms: readonly string[]): ReactNode {
  return highlightSegments(text, terms).map((segment, index) =>
    segment.match ? (
      <mark
        key={index}
        className="bg-(--app-color-accent-subtle) text-(--app-color-accent)"
      >
        {segment.text}
      </mark>
    ) : (
      <span key={index}>{segment.text}</span>
    ),
  );
}

function ResultRow({
  result,
  terms,
  rowElements,
}: {
  result: SearchResult;
  terms: readonly string[];
  rowElements: Map<string, HTMLElement>;
}): ReactNode {
  return (
    <ComboboxItem
      // Registered while drawn, dropped on unmount — a stale element would
      // anchor the card to where a row used to be.
      ref={(element) => {
        if (!element) {
          return;
        }
        rowElements.set(result.key, element);
        return () => {
          rowElements.delete(result.key);
        };
      }}
      value={result}
      // Square rather than the part's default `rounded-sm`, and a full-bleed
      // band between two rules — shared with the Create menu's rows, see
      // `ACTION_ROW_CLASS`.
      className={ACTION_ROW_CLASS}
    >
      <ActionRowContent
        {...(result.icon === undefined ? {} : { icon: result.icon })}
        title={highlightQuery(result.title, terms)}
        {...(result.personId
          ? { titleAffix: <SearchPersonAffix personId={result.personId} /> }
          : {})}
        {...(result.subtitle === undefined
          ? {}
          : { subtitle: highlightQuery(result.subtitle, terms) })}
        {...(result.badge === undefined ? {} : { badge: result.badge })}
        trailing={
          // The chord wins the slot when a command has one: it is the faster
          // way in, and the `/spelling` is still reachable by typing it.
          result.keybinding ? (
            <CommandShortcut className="flex h-5 shrink-0 items-center rounded-md bg-(--app-color-bg-inverse) px-2 text-[11px] tracking-normal text-(--app-color-text-inverse)">
              {result.keybinding}
            </CommandShortcut>
          ) : result.trailing ? (
            <span className="shrink-0 text-[11px] leading-4 text-(--app-color-text-faint)">
              {result.trailing}
            </span>
          ) : null
        }
      />
    </ComboboxItem>
  );
}

function SearchingState(): ReactNode {
  const { t } = useTranslator();
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-3 py-6">
      <Spinner
        className="size-4 text-(--app-color-text-faint)"
        aria-label={t('workbench.search.searching')}
      />
      <span className="text-[13px] leading-5 text-(--app-color-text-muted)">
        {t('workbench.search.searching')}
      </span>
    </div>
  );
}

function EmptyState({
  query,
  commandMode,
  typedCommand,
  suggestions,
  onRunSuggestion,
}: {
  query: string;
  commandMode: boolean;
  typedCommand: string;
  suggestions: readonly SearchSuggestion[];
  onRunSuggestion: (suggestion: SearchSuggestion) => void;
}): ReactNode {
  const { t, title } = useTranslator();
  return (
    // No glyph above the headline: the design carries this state on type alone,
    // and the box is only ~420px wide, where an icon crowds the two lines.
    <div className="flex flex-col items-center p-4 text-center">
      <span className="text-[13px] leading-5 font-medium">
        {commandMode
          ? t('workbench.search.unknownCommand.title', {
              command: `/${typedCommand}`,
            })
          : t('workbench.search.empty.title', { query })}
      </span>
      <span className="text-[11px] leading-4 text-(--app-color-text-faint)">
        {commandMode
          ? t('workbench.search.unknownCommand.hint')
          : t('workbench.search.empty.hint')}
      </span>
      {suggestions.length > 0 ? (
        <div className="mt-4 flex w-full flex-col items-center gap-2 border-t border-(--app-color-border) pt-4">
          <span className="text-[11px] leading-4 text-(--app-color-text-faint)">
            {t('workbench.search.unknownCommand.suggest')}
          </span>
          {suggestions.map((suggestion) => (
            <button
              key={'id' in suggestion ? suggestion.id : suggestion.key}
              type="button"
              tabIndex={-1}
              className="cursor-pointer rounded-full bg-(--app-color-accent) px-3 py-1 text-[11px] leading-4 text-(--app-color-on-accent)"
              // Same reason as the tab strip: focus must stay in the input, or
              // the combobox closes on `focusOut` and eats the click.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onRunSuggestion(suggestion)}
            >
              {title(suggestion.moduleId, suggestion.title)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Keyboard-hint footer. The two §4.2.1 entries ("view all commands" and the
 * shortcut sheet) stay off until those surfaces exist.
 */
function FooterBar(): ReactNode {
  return (
    <div className="flex shrink-0 items-center justify-end gap-1 border-t border-(--app-color-border) bg-(--app-color-bg-subtle) px-3 py-1 text-[11px] leading-4 text-(--app-color-accent)">
      {/* Filled variants: the glyph knocks the arrow out of a solid square, so
          one accent-coloured paint gives the design's two keycaps. */}
      <Icon
        size={18}
        spec={{ type: 'builtin', id: 'i-arrow-up-square-filled' }}
      />
      <Icon
        size={18}
        spec={{ type: 'builtin', id: 'i-arrow-down-square-filled' }}
      />
    </div>
  );
}

/** True for {@link WARNING_FLASH_MS} after `key` becomes non-null or changes. */
function useWarningFlash(key: string | null): boolean {
  const [flashing, setFlashing] = useState(false);
  useEffect(() => {
    if (key === null) {
      setFlashing(false);
      return;
    }
    setFlashing(true);
    const timer = setTimeout(() => setFlashing(false), WARNING_FLASH_MS);
    return () => clearTimeout(timer);
  }, [key]);
  return flashing;
}
